/**
 * build-event-prompts.js
 * Domain utilities for constructing PRECISION-style prompts for Event mode.
 */
export function buildEventPrecisionPrompt({
  usePrecision = true,
  eventType = 'Event',
  timebox = 'Unspecified duration',
  notes = '',
  persona = 'Agile Coach',
  teamLens = 'general',
  teamId = null,
  attachments = [],
  teamInstructions = null,
  teamType = null,
  apmToolLink = null,
} = {}) {
  if (!usePrecision) {
    return null;
  }

  const safeEvent = String(eventType || 'Event');
  const safeTimebox = String(timebox || 'Unspecified duration');
  const trimmedNotes = (notes || '').trim();

  const attachmentLines = Array.isArray(attachments)
    ? attachments
        .map((file) => {
          if (!file || typeof file !== 'object') {
            return null;
          }
          const name = file.name || 'file';
          const type = file.type || 'file';
          const size = typeof file.size === 'number' ? Math.round(file.size / 1024) : 0;
          return name + ' (' + type + ', ' + size + 'KB)';
        })
        .filter(Boolean)
    : [];

  const hasAttachments = attachmentLines.length > 0 || !!apmToolLink;

  const analysisParts = [];
  if (attachmentLines.length) {
    analysisParts.push('- Attached Files: ' + attachmentLines.join(', '));
  }
  if (apmToolLink) {
    analysisParts.push('- APM Tool Data: ' + apmToolLink);
  }
  if (hasAttachments) {
    analysisParts.push('- IMPORTANT: Thoroughly analyze all attached data. Extract concrete metrics (velocity, completion rates, carry-over items, trends) and weave specific findings into the facilitation agenda. Reference actual numbers from the data when making recommendations.');
  }
  if (teamInstructions) {
    analysisParts.push('- Team-Specific Instructions: ' + teamInstructions);
    analysisParts.push('- Align the plan with these team guidelines.');
  }

  const promptSections = [
    'You are an expert agile facilitator. Create a ' + safeTimebox + ' facilitation agenda for a ' + safeEvent + ' with a ' + (teamType || 'agile') + ' team.'
  ];
  if (trimmedNotes) {
    promptSections.push('Focus areas: ' + trimmedNotes);
  }
  if (analysisParts.length) {
    promptSections.push(analysisParts.join('\n'));
  }

  const formatParts = [];
  if (hasAttachments) {
    formatParts.push('Start with a brief "## Data Insights" section summarizing key findings from the attached data (metrics, trends, risks). Then provide the facilitation agenda.');
  }
  formatParts.push('Use markdown format with ## headers including timing for each segment, bullet points for activities, and expected outcomes. No preamble — start directly with content.');
  promptSections.push(formatParts.join(' '));

  return promptSections.filter(Boolean).join('\n\n');
}

export function buildEventFollowupPrecisionPrompt({
  usePrecision = true,
  followupQuestion = '',
  eventContext = {},
  persona = 'Event Facilitator',
  teamType = null,
  teamId = null,
  attachments = [],
} = {}) {
  if (!usePrecision) {
    return followupQuestion;
  }

  const question = String(followupQuestion || '').trim();
  const eventLabel = String(eventContext.event || 'event');
  const timebox = String(eventContext.timebox || 'Unspecified');
  const notes = String(eventContext.notes || 'None');

  const attachmentLines = Array.isArray(attachments)
    ? attachments
        .map((file) => {
          if (!file || typeof file !== 'object') {
            return null;
          }
          const name = file.name || 'file';
          const type = file.type || 'file';
          const size = typeof file.size === 'number' ? Math.round(file.size / 1024) : 0;
          return name + ' (' + type + ', ' + size + 'KB)';
        })
        .filter(Boolean)
    : [];

  const infoLines = [
    'PERSONA: You are an expert ' + persona + ' helping with event planning and facilitation' + (teamType ? ' for ' + teamType + ' teams' : '') + '.',
    '',
    'REASON/CONTEXT: The user has a follow-up question about their ' + eventLabel + ': "' + question + '". This matters because effective facilitation requires addressing specific concerns.',
    '',
    'INSTRUCTIONS/TASK: Provide practical, actionable guidance that directly answers the question about the ' + eventLabel + '. Focus on facilitation techniques, timing adjustments, or activity tweaks.',
    '',
    'INFORMATION:',
    '- Follow-up Question: ' + question,
    '- Event Type: ' + eventLabel,
    '- Duration: ' + timebox,
    '- Event Notes: ' + notes,
    '- Facilitation Approach: ' + persona,
  ];
  if (teamId) {
    infoLines.push('- Team Context: ' + teamId);
  }
  if (attachmentLines.length) {
    infoLines.push('- Attached Files: ' + attachmentLines.join(', '));
  }
  infoLines.push('');
  infoLines.push('CONSTRAINTS:');
  infoLines.push('- Keep guidance specific to facilitation');
  infoLines.push('- Respect the event\'s time constraints');
  infoLines.push('- Use professional facilitation language');
  infoLines.push('');
  infoLines.push('STRUCTURE: Provide clear, actionable advice addressing the user\'s specific concern.');

  return infoLines.join('\n');
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.event = window.AgileProDomain.event || {};
  window.AgileProDomain.event.buildEventPrecisionPrompt = buildEventPrecisionPrompt;
  window.AgileProDomain.event.buildEventFollowupPrecisionPrompt = buildEventFollowupPrecisionPrompt;
}
